import { Container, Grid2, Stack } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';

import List from '@components/List';
import type { ListProps } from '@components/List/List';
import ProfileCard from '@components/ProfileCard';

import { OrganizationMenuRoute } from '@constants/manage-organization';
import {
  DASHBOARD,
  ORG_BRAND_SETTINGS,
  ORG_MANAGE_ALERT_SETTINGS,
  ORG_MANAGE_DETAILS_NOTES,
  ORG_MANAGE_LICENSE_PLAN,
  ORG_MANAGE_USERS,
  ORG_PROFILE_SETTINGS,
} from '@constants/routes';

import { useAccountSettings } from '@hooks/api/account-settings';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import MainWrapper from '@views/Records/Common/MainWrapper';

import {
  useGetOrganizationProfileQuery,
  type GetOrganizationProfileQuery,
} from 'generated/account.generated';
import type { GetAccountSettingQuery } from 'generated/license.generated';

const messages = defineMessages({
  settingTitle: {
    id: 'organization.setting.title',
    defaultMessage: 'Organization Settings',
  },
  managementTitle: {
    id: 'organization.management.title',
    defaultMessage: 'Organization Management',
  },
  profile: {
    id: 'organization.setting.menu.profile',
    defaultMessage: 'Organization Profile',
  },
  userManagement: {
    id: 'organization.setting.menu.userManagement',
    defaultMessage: 'User Management',
  },
  licenseAndPlans: {
    id: 'organization.setting.menu.licenseAndPlans',
    defaultMessage: 'License and Plans',
  },
  alertSetting: {
    id: 'organization.setting.menu.alertSetting',
    defaultMessage: 'Alert Settings',
  },
  brandSetting: {
    id: 'organization.setting.menu.brandSetting',
    defaultMessage: 'Brand Settings',
  },
  cardSubtitle: {
    id: 'organization.setting.profile.subtitle',
    defaultMessage: 'Organization',
  },
  ['management.menu.alertSettings']: {
    id: 'organization.management.menu.alertSettings',
    defaultMessage: 'Alert Settings',
  },
  ['management.menu.brandSettings']: {
    id: 'organization.management.menu.brandSettings',
    defaultMessage: 'Brand Settings',
  },
  ['management.menu.dataRetention']: {
    id: 'organization.management.menu.dataRetention',
    defaultMessage: 'Data Retention',
  },
  ['management.menu.detailsAndNotes']: {
    id: 'organization.management.menu.detailsAndNotes',
    defaultMessage: 'Details and Notes',
  },
  ['management.menu.licensePlanManagement']: {
    id: 'organization.management.menu.licensePlanManagement',
    defaultMessage: 'License/Plan Management',
  },
  ['management.menu.tasks']: {
    id: 'organization.management.menu.tasks',
    defaultMessage: 'Tasks',
  },
  ['management.menu.userManagement']: {
    id: 'organization.management.menu.userManagement',
    defaultMessage: 'User Management',
  },
});

interface ManageOrgLayoutChildrenProps {
  account_id: string;
  account?: GetOrganizationProfileQuery['getAccountRecord'];
  accountSettings?: GetAccountSettingQuery;
  loading?: boolean;
  /** True if logged in user is org admin. False if logged in user is from VITALL */
  is_within_org: boolean;
}
interface OrgSettingLayoutProps {
  children: (props: ManageOrgLayoutChildrenProps) => React.ReactNode;
  pageProps: PagePropsWithBreadcrumbs;
}

interface OrgSettingContentProps {
  children: (props: ManageOrgLayoutChildrenProps) => React.ReactNode;
  pageProps: PagePropsWithBreadcrumbs;
  org_account_id?: string | null;
}

function OrgSettingContent({
  children,
  pageProps,
  org_account_id,
}: OrgSettingContentProps) {
  const { formatMessage } = useIntl();
  const { navigateRoute } = useNavigate();
  const {
    state: { isLoadingAuth, loggedInProfile, isAdmin },
  } = useSession();

  // `useSearchParam` gives null when `account_id` is absent — use the viewer's
  // own org — and '' when it is present but valueless. Neither case has an org
  // to show, so fall back to the dashboard.
  const account_id = org_account_id ?? loggedInProfile?.account_id ?? '';
  const hasNoAccount = !isLoadingAuth && !account_id;

  React.useEffect(() => {
    if (hasNoAccount) {
      navigateRoute(DASHBOARD);
    }
    // `navigateRoute` is rebuilt every render, so depending on it would re-fire
    // this on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNoAccount]);

  const { data: accountData, loading } = useGetOrganizationProfileQuery({
    variables: {
      input: {
        account_id,
      },
    },
    skip: !account_id,
  });

  const { accountSettings } = useAccountSettings(account_id);
  const organization = accountData?.getAccountRecord;

  const settingItems: ListProps['items'] = [
    {
      title: formatMessage(messages.profile),
      handleClick: () => navigate(ORG_PROFILE_SETTINGS),
      selected: pageProps.path.includes(OrganizationMenuRoute.Profile),
    },
    {
      title: formatMessage(messages.userManagement),
      handleClick: () => navigate(ORG_MANAGE_USERS),
      selected: pageProps.path.includes(OrganizationMenuRoute.UserManagement),
    },
    {
      title: formatMessage(messages.licenseAndPlans),
      handleClick: () => navigate(ORG_MANAGE_LICENSE_PLAN),
      selected: pageProps.path.includes(OrganizationMenuRoute.LicensePlan),
    },
    {
      title: formatMessage(messages.alertSetting),
      handleClick: () => navigate(ORG_MANAGE_ALERT_SETTINGS),
      selected: pageProps.path.includes(OrganizationMenuRoute.Alerts),
    },
    ...(!accountSettings?.getAccountSetting?.license?.enabled_branding
      ? []
      : [
          {
            title: formatMessage(messages.brandSetting),
            handleClick: () => navigate(ORG_BRAND_SETTINGS),
            selected: pageProps.path.includes(OrganizationMenuRoute.Brand),
          },
        ]),
  ];

  const managementItems: ListProps['items'] = [
    {
      title: formatMessage(messages['management.menu.detailsAndNotes']),
      handleClick: () =>
        navigateRoute(ORG_MANAGE_DETAILS_NOTES, { account_id }),
      selected: pageProps.path.includes(OrganizationMenuRoute.DetailsAndNotes),
    },
    {
      title: formatMessage(messages['management.menu.licensePlanManagement']),
      handleClick: () => navigateRoute(ORG_MANAGE_LICENSE_PLAN, { account_id }),
      selected: pageProps.path.includes(OrganizationMenuRoute.LicensePlan),
    },
    {
      title: formatMessage(messages['management.menu.userManagement']),
      handleClick: () => navigateRoute(ORG_MANAGE_USERS, { account_id }),
      selected: pageProps.path.includes(OrganizationMenuRoute.UserManagement),
    },
    {
      disabled: true,
      title: formatMessage(messages['management.menu.dataRetention']),
      selected: pageProps.path.includes(OrganizationMenuRoute.DataRetention),
    },
    {
      title: formatMessage(messages['management.menu.alertSettings']),
      handleClick: () =>
        navigateRoute(ORG_MANAGE_ALERT_SETTINGS, { account_id }),
      selected: pageProps.path.includes(OrganizationMenuRoute.Alerts),
    },
    {
      disabled: true,
      title: formatMessage(messages['management.menu.tasks']),
      selected: pageProps.path.includes(OrganizationMenuRoute.Tasks),
    },
    {
      disabled: !(
        isAdmin && accountSettings?.getAccountSetting?.license?.enabled_branding
      ),
      title: formatMessage(messages['management.menu.brandSettings']),
      handleClick: () => navigateRoute(ORG_BRAND_SETTINGS, { account_id }),
      selected: pageProps.path.includes(OrganizationMenuRoute.Brand),
    },
  ];

  // The effect above is navigating away; rendering the settings chrome against
  // no account would only flash an empty page first.
  if (hasNoAccount) {
    return null;
  }

  return (
    <Container maxWidth="xl">
      <Grid2 container spacing={3}>
        <Grid2 size={{ md: 4, xs: 12 }}>
          <Stack spacing={2}>
            <ProfileCard
              fallback_profile_picture="users"
              loading={loading}
              name={organization?.account_name}
              profile_picture={organization?.profile_picture}
              subtitle={formatMessage(messages.cardSubtitle)}
            />

            <List
              type="org-setting-list"
              items={org_account_id ? managementItems : settingItems}
            />
          </Stack>
        </Grid2>

        <Grid2 size={{ md: 8, xs: 12 }}>
          {children({
            account_id,
            account: organization,
            accountSettings,
            loading,
            is_within_org: Boolean(!org_account_id),
          })}
        </Grid2>
      </Grid2>
    </Container>
  );
}

/* eslint-disable react/no-children-prop */
export default function OrgSettingManagementLayout({
  children,
  pageProps,
}: OrgSettingLayoutProps) {
  const {
    breadcrumb: { crumbs },
  } = pageProps.pageContext;
  const org_account_id = useSearchParam('account_id');
  const { formatMessage } = useIntl();

  return (
    <MainWrapper
      friendlyName={crumbs.at(-1)?.crumbLabel}
      title={formatMessage(
        org_account_id ? messages.managementTitle : messages.settingTitle
      )}
    >
      <OrgSettingContent
        children={children}
        org_account_id={org_account_id}
        pageProps={pageProps}
      />
    </MainWrapper>
  );
}
